/**
 * textDiff.ts — dependency-free LCS word-level diff.
 *
 * diffWords(original, edited) → DiffToken[]
 * Tokens are marked equal | insert | delete using a classic LCS DP table.
 * Designed for the WS-W7 EditedDiff component; no external deps required.
 */

export type DiffToken = {
  text: string;
  type: "equal" | "insert" | "delete";
};

/** Tokenise a string into words (preserving trailing whitespace per word). */
function tokenise(s: string): string[] {
  // Split on whitespace boundaries, preserving the space as part of the
  // preceding token so round-tripping the tokens gives the original string.
  return s.match(/\S+\s*/g) ?? [];
}

/** Build the LCS length table for two token arrays. */
function buildLcs(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  // Allocate table row by row to avoid one giant allocation.
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

/** Back-track through the LCS table to emit diff tokens. */
function backtrack(
  dp: number[][],
  a: string[],
  b: string[],
  i: number,
  j: number,
  out: DiffToken[],
): void {
  // Iterative stack-based backtrack to avoid call-stack overflow on long texts.
  const stack: Array<{ i: number; j: number }> = [];
  let ci = i;
  let cj = j;
  while (ci > 0 || cj > 0) {
    if (ci > 0 && cj > 0 && a[ci - 1] === b[cj - 1]) {
      stack.push({ i: ci, j: cj });
      ci--;
      cj--;
    } else if (cj > 0 && (ci === 0 || dp[ci][cj - 1] >= dp[ci - 1][cj])) {
      stack.push({ i: -cj, j: cj }); // negative i = insert from b
      cj--;
    } else {
      stack.push({ i: ci, j: -cj }); // negative j = delete from a
      ci--;
    }
  }
  // Emit in forward order (stack is reversed).
  while (stack.length > 0) {
    const frame = stack.pop()!;
    if (frame.i > 0 && frame.j > 0) {
      out.push({ text: a[frame.i - 1], type: "equal" });
    } else if (frame.i <= 0 && frame.j > 0) {
      out.push({ text: b[frame.j - 1], type: "insert" });
    } else if (frame.i > 0 && frame.j <= 0) {
      out.push({ text: a[frame.i - 1], type: "delete" });
    }
  }
}

/**
 * Compute a word-level diff between `original` and `edited`.
 * Returns a flat array of DiffToken in document order.
 *
 * @example
 * diffWords("hello world", "hello there")
 * // → [{ text: "hello ", type: "equal" },
 * //    { text: "world", type: "delete" },
 * //    { text: "there", type: "insert" }]
 */
export function diffWords(original: string, edited: string): DiffToken[] {
  const a = tokenise(original);
  const b = tokenise(edited);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return b.map((t) => ({ text: t, type: "insert" }));
  if (b.length === 0) return a.map((t) => ({ text: t, type: "delete" }));

  const dp = buildLcs(a, b);
  const tokens: DiffToken[] = [];
  backtrack(dp, a, b, a.length, b.length, tokens);
  return tokens;
}
